import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FC, Fragment, useState } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import {
  Button,
  FieldRow,
  Flex,
  Modal,
  Spacer,
  StyledFormGroup,
  StyledFormikDatePicker,
  StyledFormSelectFormik,
  WrappedQuery,
} from '../../../../../components/shared';
import {
  ALL_COMMUNITY_TYPES,
  ALL_DISTRICT_MUNICIPALITIES,
  ALL_LOCAL_MUNICIPALITIES,
  ALL_PROVINCES,
  FILTERED_INSTITUTIONS_SELECT,
  FILTERED_REASON_FOR_TERMINATIONS,
  FILTERED_STAKEHOLDER_ROLES,
} from '../../../../../graphql';
import { Option, PeopleProfileInstitutionFormType } from '../../../../../utils/types';
import { formatSelectValue, formatValues } from '../../../../../utils';
import { OperationVariables, QueryResult } from '@apollo/client';

const Actions = styled.div`
  display: flex;
`;

type EditCommunityModalFormPropsType = {
  data: PeopleProfileInstitutionFormType;
  parentValues: any | null;
  onClose: (cancel: boolean, data: PeopleProfileInstitutionFormType) => void;
  open: boolean;
  viewMode?: boolean;
  isLearner?: boolean;
  typeId?: any;
  allInstitutionTypes?: any;
  allStakeholderTypes?: any;
  allGrades?: any;
};

const peopleProfileInstitutionFormTypeDefault: PeopleProfileInstitutionFormType = {
  id: '',
  designation: '',
  startDate: new Date('1 January 1970'),
  endDate: new Date('31 December 9999'),
  postLevel: undefined,
  primary: false,
  typeId: undefined,
  positionId: undefined,
  positionOther: '',
  phaseIds: [],
  gradeIds: [],
  subjectIds: [],
  institution: undefined,
  progressionStatusId: undefined,
  year: undefined,
  registrationDate: null,
  dateExited: null,
  trackingId: '',
  ageCohort: undefined,
  reasonForTerminationId: undefined,
  linkedToAnInstitution: undefined,
  linkedToAnOrganisation: undefined,
  roleId: undefined,
};

const EditCommunityModalFormPropsTypeDefault: EditCommunityModalFormPropsType = {
  data: peopleProfileInstitutionFormTypeDefault,
  parentValues: null,
  onClose: (cancel: boolean, data: PeopleProfileInstitutionFormType) => {},
  open: false,
  viewMode: true,
};

type FormType = PeopleProfileInstitutionFormType & {
  // fields not for saving
  institutionTypeId: Option | undefined;
  institutionId:
    | (Option & {
        id: string;
        name: string;
        emisNumber: string;
      })
    | undefined;
  provinceId?: any;
  districtId?: any;
  districtMunicipalityId?: any;
  localMunicipalityId?: any;
  communityTypeId?: any;
};

const EditCommunityModalForm: FC<EditCommunityModalFormPropsType> = props => {
  const viewMode = props.viewMode === true;
  const isNew = props.data.id === '';

  const initialValues: FormType = {
    ...props.data,
    institutionTypeId:
      !isNew && props.data.institution && props.data.institution.type
        ? {
            label: props.data.institution.type.name,
            value: props.data.institution.type.id,
          }
        : props.allInstitutionTypes &&
          isNew &&
          props.allInstitutionTypes.find((b: any) => b.label && b.label.toLowerCase() === 'community'),
    institutionId:
      !isNew && props.data.institution
        ? {
            label: props.data.institution.name,
            value: props.data.institution.id,
            name: props.data.institution.name,
            id: props.data.institution.id,
            emisNumber: props.data.institution.emisNumber,
          }
        : undefined,
    ...(!isNew &&
      props.data.institution && {
        districtId: formatValues(props.data.institution.district),
        districtMunicipalityId: formatValues(props.data.institution.districtMunicipality),
        provinceId: formatValues(props.data.institution.province),
        localMunicipalityId: formatValues(props.data.institution.localMunicipality),
        communityTypeId: formatValues(props.data.institution.communityType),
      }),
    ...(isNew && {
      id: 'create' + new Date().getTime().toString(),
      endDate: new Date(new Date().getFullYear(), 11, 31),
      startDate: new Date(new Date().getFullYear(), 0, 1),
      typeId: props.typeId,
    }),
  };

  const validationSchema = yup.object().shape({
    institutionId: yup
      .object()
      .label('Community')
      .required(),
    roleId: yup
      .object()
      .label('Role')
      .required(),
    // gradeIds: yup
    //   .array()
    //   .label('Grade')
    //   .when('typeId', {
    //     is: typeId => typeId && typeId.label === 'Learners',
    //     then: yup.array().required(),
    //   }),
    startDate: yup
      .date()
      .label('Start Date')
      .required(),
    endDate: yup
      .date()
      .label('End Date')
      .required(),
  });

  return (
    <Fragment>
      <Modal
        open={props.open}
        onClose={() => props.onClose(true, props.data)}
        heading={isNew ? 'Link to Community' : 'Edit Community Association'}
        width="800px"
      >
        {props.open && (
          <Flex direction="column" size="1">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={values => {
                const result: any = { ...values };
                if (values.institutionId && values.institutionTypeId) {
                  result.institution = {
                    id: values.institutionId.value,
                    name: values.institutionId.label,
                    emisNumber: values.institutionId.emisNumber || '',
                    district: values.districtId,
                    type: {
                      id: values.institutionTypeId.value,
                      name: values.institutionTypeId.label,
                    },
                    province: values.provinceId,
                    localMunicipality: values.localMunicipalityId,
                    communityType: values.communityTypeId,
                    districtMunicipality: values.districtMunicipalityId,
                  };
                }
                delete result['institutionTypeId'];
                delete result['institutionId'];
                props.onClose(false, result);
              }}
              render={(formikProps: FormikProps<FormType>) => {
                const values = formikProps.values;
                return (
                  <Fragment>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <WrappedQuery query={ALL_PROVINCES} variables={{ input: {} }} overrideStates={true}>
                          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                            return (
                              <Field
                                name="provinceId"
                                render={({ field }: FieldProps<FormType>) => (
                                  <StyledFormSelectFormik
                                    {...field}
                                    field={field}
                                    label="Province"
                                    form={formikProps}
                                    readOnly={!isNew || viewMode}
                                    options={
                                      loading || error
                                        ? []
                                        : data.provinces.map((item: any) =>
                                            formatSelectValue({
                                              label: item.name,
                                              value: item.id,
                                            })
                                          )
                                    }
                                    mr={4}
                                  />
                                )}
                              />
                            );
                          }}
                        </WrappedQuery>
                      </FieldRow>
                      <FieldRow flex={0.5}>
                        <WrappedQuery
                          query={ALL_DISTRICT_MUNICIPALITIES}
                          variables={{
                            input: {
                              provinceId: values.provinceId?.value,
                            },
                          }}
                          overrideStates={true}
                        >
                          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                            return (
                              <Field
                                name="districtMunicipalityId"
                                render={({ field }: FieldProps<FormType>) => (
                                  <StyledFormSelectFormik
                                    {...field}
                                    field={field}
                                    label="District Municipality"
                                    form={formikProps}
                                    readOnly={!isNew || viewMode}
                                    options={
                                      loading || error
                                        ? []
                                        : data.municipalities.map((item: any) =>
                                            formatSelectValue({
                                              label: item.name,
                                              value: item.id,
                                            })
                                          )
                                    }
                                    mr={4}
                                  />
                                )}
                              />
                            );
                          }}
                        </WrappedQuery>
                      </FieldRow>
                    </FieldRow>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <WrappedQuery
                          query={ALL_LOCAL_MUNICIPALITIES}
                          variables={{
                            input: {
                              districtMunicipalityId: values.districtMunicipalityId?.value,
                            },
                          }}
                          overrideStates={true}
                        >
                          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                            return (
                              <Field
                                name="localMunicipalityId"
                                render={({ field }: FieldProps<FormType>) => (
                                  <StyledFormSelectFormik
                                    {...field}
                                    field={field}
                                    label="Local Municipality"
                                    form={formikProps}
                                    readOnly={!isNew || viewMode}
                                    options={
                                      loading || error
                                        ? []
                                        : data.municipalities &&
                                          data.municipalities.map((item: any) =>
                                            formatSelectValue({
                                              label: item.localMunicipalityShorthand,
                                              value: item.id,
                                            })
                                          )
                                    }
                                    mr={4}
                                  />
                                )}
                              />
                            );
                          }}
                        </WrappedQuery>
                      </FieldRow>
                      <FieldRow flex={0.5}>
                        <WrappedQuery query={ALL_COMMUNITY_TYPES} overrideStates={true}>
                          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                            return (
                              <Field
                                name="communityTypeId"
                                render={({ field }: FieldProps<FormType>) => (
                                  <StyledFormSelectFormik
                                    {...field}
                                    field={field}
                                    label="Community Type"
                                    form={formikProps}
                                    readOnly={!isNew || viewMode}
                                    options={
                                      loading || error
                                        ? []
                                        : data.communityTypes.map((item: any) =>
                                            formatSelectValue({
                                              label: item.name,
                                              value: item.id,
                                            })
                                          )
                                    }
                                    mr={4}
                                  />
                                )}
                              />
                            );
                          }}
                        </WrappedQuery>
                      </FieldRow>
                    </FieldRow>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          name="institutionId"
                          render={({ field }: any) => (
                            <StyledFormSelectFormik
                              {...field}
                              field={field}
                              label="Community"
                              placeholder={'Select Community...'}
                              readOnly={!values.institutionTypeId || !isNew || viewMode}
                              form={formikProps}
                              isMulti={false}
                              options={isNew ? undefined : [values.institutionId]}
                              mr={4}
                              query={{
                                query: FILTERED_INSTITUTIONS_SELECT,
                                variables: {
                                  input: {
                                    first: 20,
                                    ...(isNew && {
                                      typeId: values.institutionTypeId ? values.institutionTypeId.value : 'none',
                                      provinceId: values.provinceId?.value,
                                      districtId: values.districtId?.value,
                                      localMunicipalityId: values.localMunicipalityId?.value,
                                      communityTypeId: values.communityTypeId?.value,
                                    }),
                                    ...(!isNew &&
                                      initialValues.institutionId && {
                                        id: initialValues.institutionId.value,
                                      }),
                                  },
                                },
                                searchTextField: 'input.name',
                                resultItemOptions: {
                                  copyAllItemFields: true,
                                },
                                skip: !isNew,
                              }}
                            />
                          )}
                        />
                      </FieldRow>
                      {values.institutionTypeId &&
                        (values.institutionTypeId.label === 'School' ||
                          values.institutionTypeId.label === 'ECD Service') && (
                          <FieldRow flex={0.5}>
                            <Field
                              name="institutionId.emisNumber"
                              render={({ field }: any) => (
                                <StyledFormGroup
                                  {...field}
                                  field={field}
                                  form={formikProps}
                                  label="EMIS Number"
                                  type="number"
                                  // mr={4}
                                  readOnly={true}
                                />
                              )}
                            />
                          </FieldRow>
                        )}
                    </FieldRow>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          name="primary"
                          render={({ field }: FieldProps<FormType>) => (
                            <StyledFormGroup
                              {...field}
                              field={field}
                              form={formikProps}
                              label="Primary Community"
                              type="checkbox"
                              readOnly={viewMode}
                              mr={4}
                              defaultChecked={values.primary}
                              onChange={(option: any) => {
                                formikProps.setFieldValue('primary', !values.primary);
                              }}
                              wrapperWidth="40px"
                              width="40px"
                            />
                          )}
                        />
                      </FieldRow>
                    </FieldRow>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <WrappedQuery
                          query={FILTERED_STAKEHOLDER_ROLES}
                          variables={{
                            input: {
                              typeId: props.typeId?.value,
                            },
                          }}
                          overrideStates={true}
                        >
                          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                            return (
                              <Field
                                name="roleId"
                                render={({ field }: FieldProps<FormType>) => (
                                  <StyledFormSelectFormik
                                    {...field}
                                    field={field}
                                    label="Role"
                                    form={formikProps}
                                    options={
                                      loading || error
                                        ? []
                                        : data.filteredStakeholderRoles &&
                                          data.filteredStakeholderRoles.map((item: any) =>
                                            formatSelectValue({
                                              label: item.name,
                                              value: item.id,
                                            })
                                          )
                                    }
                                  />
                                )}
                              />
                            );
                          }}
                        </WrappedQuery>
                      </FieldRow>
                    </FieldRow>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          render={() => (
                            <StyledFormikDatePicker
                              isSingleDate={true}
                              form={formikProps}
                              label="Date Exited"
                              dateLabel="dateExited"
                              date={values.dateExited}
                              clearIcon={null}
                              format={'d-M-yyyy'}
                              readOnly={viewMode}
                              mr={4}
                            />
                          )}
                        />
                      </FieldRow>
                      <FieldRow flex={0.5}>
                        <WrappedQuery
                          query={FILTERED_REASON_FOR_TERMINATIONS}
                          variables={{
                            input: {
                              typeId: props.typeId?.value,
                            },
                          }}
                          overrideStates={true}
                        >
                          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                            return (
                              <Field
                                name="reasonForTerminationId"
                                render={({ field }: FieldProps<FormType>) => (
                                  <StyledFormSelectFormik
                                    {...field}
                                    field={field}
                                    label="Reason for Exit"
                                    form={formikProps}
                                    options={
                                      loading || error
                                        ? []
                                        : data.filteredReasonForTerminations &&
                                          data.filteredReasonForTerminations.map((item: any) =>
                                            formatSelectValue({
                                              label: item.name,
                                              value: item.id,
                                            })
                                          )
                                    }
                                  />
                                )}
                              />
                            );
                          }}
                        </WrappedQuery>
                      </FieldRow>
                    </FieldRow>
                    <Spacer spacing={15} />
                    <FieldRow justifyContent={'flex-end'}>
                      <Flex direction="row" size="1" justify="flex-end">
                        <Actions>
                          {!viewMode ? (
                            <Fragment>
                              <Button
                                type="button"
                                text="Cancel"
                                mr={3}
                                onClick={() => props.onClose(true, props.data)}
                              />
                              <Button type="button" text="Save" onClick={() => formikProps.handleSubmit()} />
                            </Fragment>
                          ) : (
                            <Fragment>
                              <Button
                                type="button"
                                text="Close"
                                mr={0}
                                onClick={() => props.onClose(true, props.data)}
                              />
                            </Fragment>
                          )}
                        </Actions>
                      </Flex>
                    </FieldRow>
                  </Fragment>
                );
              }}
            />
          </Flex>
        )}
      </Modal>
    </Fragment>
  );
};

export const useEditCommunityModalForm = (): [
  React.FC,
  (
    data: PeopleProfileInstitutionFormType,
    parentValues: any | null,
    onDone: (cancel: boolean, data: PeopleProfileInstitutionFormType) => void,
    isLearner?: boolean,
    typeId?: any,
    allInstitutionTypes?: any,
    allStakeholderTypes?: any,
    allGrades?: any
  ) => void
] => {
  const [showModal, setShowModal] = useState<EditCommunityModalFormPropsType>(EditCommunityModalFormPropsTypeDefault);

  const show = (
    data: PeopleProfileInstitutionFormType,
    parentValues: any | null,
    onDone: (cancel: boolean, data: PeopleProfileInstitutionFormType) => void,
    isLearner?: boolean,
    typeId?: any,
    allInstitutionTypes?: any,
    allStakeholderTypes?: any,
    allGrades?: any
  ) => {
    if (!showModal.open) {
      setShowModal({
        data: data,
        parentValues: parentValues,
        open: true,
        isLearner: isLearner,
        typeId,
        allInstitutionTypes,
        allStakeholderTypes,
        allGrades,
        onClose: (cancel: boolean, data: PeopleProfileInstitutionFormType) => {
          onDone(cancel, data);
          setShowModal(EditCommunityModalFormPropsTypeDefault);
        },
      });
    }
  };

  return [
    () => {
      return <Fragment>{showModal.open && <EditCommunityModalForm {...showModal} />}</Fragment>;
    },
    show,
  ];
};
